import React, { useCallback, useEffect, useMemo, useState } from "react";
import FeatherIcon from "feather-icons-react";
import {
  discardQueuedRequest,
  getCachedResponse,
  getOfflinePreparation,
  getQueueCount,
  getQueueItems,
  getQueuedRequestRemovalImpact,
  retryQueuedRequest,
} from "../offline/database";
import {
  isOfflinePreparationCurrent,
  preloadOfflineData,
  syncOfflineQueue,
} from "../offline/apiOffline";
import {
  buildQueueDependencyMap,
  getQueueItemMetadata,
} from "../offline/queueMetadata";

const PAGE_SIZE = 30;

const STATUS_LABELS = {
  pending: "En attente",
  syncing: "Synchronisation",
  failed: "Nouvel essai programmé",
  blocked: "Action requise",
};

function OfflineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [items, setItems] = useState([]);
  const [queueReferences, setQueueReferences] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [preparation, setPreparation] = useState(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [preparationError, setPreparationError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);

  const refreshQueue = useCallback(async () => {
    try {
      const [count, queueItems, ...referenceEntries] = await Promise.all([
        getQueueCount(),
        getQueueItems(),
        ...[
          "staffs",
          "students",
          "days",
          "years",
          "sections",
          "sectionclasses",
          "classes",
          "schoolfeecategories",
          "months",
          "trimesters",
        ].map((resource) =>
          getCachedResponse(
            { method: "get", url: resource, params: {} },
            { allowExpired: true }
          )
            .then((cached) => [resource, cached?.data])
            .catch(() => [resource, undefined])
        ),
      ]);
      setPending(count);
      setItems(queueItems);
      setQueueReferences(Object.fromEntries(referenceEntries));
    } catch {
      setPending(0);
      setItems([]);
      setQueueReferences({});
    }
  }, []);

  const refreshPreparation = useCallback(async () => {
    try {
      setPreparation(await getOfflinePreparation());
    } catch {
      setPreparation(null);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      refreshQueue();
    };
    const handleOffline = () => setIsOnline(false);
    const handleSyncStarted = () => setSyncing(true);
    const handleSyncFinished = () => {
      setSyncing(false);
      refreshQueue();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("offline:queue-changed", refreshQueue);
    window.addEventListener("offline:sync-started", handleSyncStarted);
    window.addEventListener("offline:sync-finished", handleSyncFinished);
    refreshQueue();
    refreshPreparation();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("offline:queue-changed", refreshQueue);
      window.removeEventListener("offline:sync-started", handleSyncStarted);
      window.removeEventListener("offline:sync-finished", handleSyncFinished);
    };
  }, [refreshQueue, refreshPreparation]);

  const prepareOfflineData = async () => {
    setPreparing(true);
    setPreparationError("");
    setProgress({ completed: 0, total: 0 });

    try {
      const summary = await preloadOfflineData({ onProgress: setProgress });
      await refreshPreparation();
      if (summary.failures.length) {
        setPreparationError(
          `${summary.failures.length} ressource(s) n'ont pas pu être téléchargée(s).`
        );
      }
    } catch (error) {
      setPreparationError(error.message || "Préparation hors ligne impossible.");
    } finally {
      setPreparing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const prepareIfNeeded = async (force = false) => {
      try {
        const current = await getOfflinePreparation();
        const isFresh =
          isOfflinePreparationCurrent(current) &&
          current?.updatedAt &&
          Date.now() - current.updatedAt < 6 * 60 * 60 * 1000;

        if (!navigator.onLine || (!force && isFresh)) return;
        if (!cancelled) {
          setPreparing(true);
          setPreparationError('');
          setProgress({ completed: 0, total: 0 });
        }

        const summary = await preloadOfflineData({
          onProgress: (nextProgress) => {
            if (!cancelled) setProgress(nextProgress);
          },
        });

        if (!cancelled) {
          setPreparation(await getOfflinePreparation());
          if (summary.failures.length) {
            setPreparationError(
              `${summary.failures.length} ressource(s) RH n'ont pas pu être téléchargée(s).`
            );
          }
        }
      } catch (error) {
        if (!cancelled) {
          setPreparationError(error.message || 'Préparation hors ligne impossible.');
        }
      } finally {
        if (!cancelled) setPreparing(false);
      }
    };

    const handleOnline = () => prepareIfNeeded();
    const handleRefreshNeeded = () => prepareIfNeeded(true);
    prepareIfNeeded();
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline:data-refresh-needed', handleRefreshNeeded);

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline:data-refresh-needed', handleRefreshNeeded);
    };
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [expanded]);

  const retry = async (id) => {
    await retryQueuedRequest(id);
    if (isOnline) await syncOfflineQueue({ force: true });
  };

  const discard = async (id) => {
    const impacted = await getQueuedRequestRemovalImpact(id);
    const dependents = impacted.slice(1);
    const message = dependents.length
      ? `Cette donnée est utilisée par ${dependents.length} autre(s) opération(s) en attente. La supprimer retirera aussi :\n\n${dependents
          .slice(0, 8)
          .map((item) => `• ${getQueueItemMetadata(item).title}`)
          .join("\n")}${dependents.length > 8 ? `\n• … et ${dependents.length - 8} autre(s)` : ""}\n\nContinuer ?`
      : "Supprimer définitivement cette modification en attente ?";
    if (!window.confirm(message)) return;
    await discardQueuedRequest(id);
  };

  const dependencyMap = useMemo(() => buildQueueDependencyMap(items), [items]);
  const itemById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );
  const statusCounts = useMemo(
    () =>
      items.reduce(
        (counts, item) => ({
          ...counts,
          [item.status]: (counts[item.status] || 0) + 1,
        }),
        {}
      ),
    [items]
  );
  const filteredItems = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("fr");
    return items.filter((item) => {
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (!needle) return true;
      const metadata = getQueueItemMetadata(item, queueReferences);
      return `${metadata.title} ${metadata.description} ${metadata.operationLabel} ${item.error || ""}`
        .toLocaleLowerCase("fr")
        .includes(needle);
    });
  }, [items, queueReferences, search, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visibleItems = filteredItems.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const label = !isOnline
    ? `Hors ligne${pending ? ` · ${pending} en attente` : ""}`
    : preparing
      ? `Préparation ${progress.completed}/${progress.total}`
      : syncing
        ? "Synchronisation…"
        : pending
          ? `${pending} modification${pending > 1 ? "s" : ""} en attente`
          : preparation && isOfflinePreparationCurrent(preparation)
            ? "Données offline prêtes"
            : "Préparer offline";

  const statusButtonClass = !isOnline
    ? "btn-danger"
    : pending || preparing || syncing
      ? "btn-warning"
      : "btn-outline-primary";

  return (
    <div className="offline-status">
      {expanded && (
        <>
          <div className="offline-status__scrim" onClick={() => setExpanded(false)} aria-hidden="true" />
          <aside className="offline-status__panel" aria-label="Mode hors ligne">
          <div className="offline-status__header">
            <span className={`offline-status__logo ${!isOnline ? "is-offline" : ""}`}><FeatherIcon icon={!isOnline ? "wifi-off" : "database"} size="21" /></span>
            <div><strong>Mode hors ligne</strong><small>{isOnline ? "Connexion disponible" : "Travail sans connexion"}</small></div>
            <button
              type="button"
              className="offline-status__close"
              onClick={() => setExpanded(false)}
              aria-label="Fermer"
            >
              <FeatherIcon icon="x" size="20" />
            </button>
          </div>

          <div className="offline-status__content">
            <p className="small mb-2">
              {preparation
                ? `Dernière préparation : ${new Date(preparation.updatedAt).toLocaleString()}`
                : "Les données essentielles n'ont pas encore été préparées."}
            </p>
            {preparationError && (
              <div className="alert alert-warning small py-2 mb-2">
                <div>{preparationError}</div>
                {preparation?.failures?.length > 0 && (
                  <ul className="mb-0 mt-1 ps-3">
                    {preparation.failures.map((failure) => (
                      <li key={failure.resource}>
                        <strong>{failure.resource}</strong>
                        {failure.error ? ` : ${failure.error}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-primary w-100"
              onClick={prepareOfflineData}
              disabled={!isOnline || preparing || syncing}
            >
              <FeatherIcon icon="download-cloud" size="14" className="me-1" />
              {preparing ? "Téléchargement…" : "Préparer les données offline"}
            </button>
          </div>

          {items.length > 0 && (
            <>
              <div className="border-top px-3 py-2 bg-light">
                <div className="d-flex flex-wrap gap-2 small mb-2">
                  <span className="badge bg-secondary">{items.length} total</span>
                  {statusCounts.blocked > 0 && (
                    <span className="badge bg-danger">{statusCounts.blocked} à corriger</span>
                  )}
                  {statusCounts.failed > 0 && (
                    <span className="badge bg-warning text-dark">{statusCounts.failed} à réessayer</span>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text"><FeatherIcon icon="search" size="13" /></span>
                    <input
                      className="form-control"
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(1);
                      }}
                      placeholder="Nom, type ou erreur…"
                      aria-label="Rechercher dans les données en attente"
                    />
                  </div>
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: 155 }}
                    value={statusFilter}
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      setPage(1);
                    }}
                    aria-label="Filtrer par état"
                  >
                    <option value="all">Tous les états</option>
                    <option value="pending">En attente</option>
                    <option value="failed">À réessayer</option>
                    <option value="blocked">À corriger</option>
                  </select>
                </div>
              </div>
              <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: 360 }}>
                {visibleItems.map((item) => {
                  const metadata = getQueueItemMetadata(item, queueReferences);
                  const dependencies = (dependencyMap.get(item.id) || [])
                    .map((id) => itemById.get(id))
                    .filter(Boolean);
                  return (
                    <div className="list-group-item py-2" key={item.id}>
                      <div className="d-flex justify-content-between gap-3">
                        <div className="small text-break flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="d-flex flex-wrap align-items-center gap-1">
                            <strong>{metadata.title}</strong>
                            <span className="badge bg-light text-dark border">
                              {metadata.operationLabel}
                            </span>
                          </div>
                          <div className={item.status === "blocked" ? "text-danger" : "text-muted"}>
                            {STATUS_LABELS[item.status] || item.status}
                            {item.attempts ? ` · ${item.attempts} essai(s)` : ""}
                            {" · "}
                            {new Date(item.createdAt).toLocaleString("fr-FR")}
                          </div>
                          {metadata.description && (
                            <div className="text-muted">{metadata.description}</div>
                          )}
                          {dependencies.length > 0 && (
                            <div className="text-primary">
                              Dépend de {dependencies.map((dependency) => getQueueItemMetadata(dependency).title).join(", ")}
                            </div>
                          )}
                          {item.error && <div className="text-danger mt-1">{item.error}</div>}
                        </div>
                        <div className="d-flex align-items-start gap-1">
                          {["failed", "blocked"].includes(item.status) &&
                            item.blockedReason !== "dependency" && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary p-1"
                                onClick={() => retry(item.id)}
                                disabled={!isOnline || syncing}
                                title="Réessayer"
                              >
                                <FeatherIcon icon="rotate-cw" size="14" />
                              </button>
                            )}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger p-1"
                            onClick={() => discard(item.id)}
                            disabled={item.status === "syncing"}
                            title="Supprimer l'opération locale"
                          >
                            <FeatherIcon icon="trash-2" size="14" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {visibleItems.length === 0 && (
                  <div className="list-group-item small text-muted text-center py-3">
                    Aucune opération ne correspond à ce filtre.
                  </div>
                )}
              </div>
              {pageCount > 1 && (
                <div className="d-flex justify-content-between align-items-center border-top px-3 py-2 small">
                  <span>{filteredItems.length} résultat(s) · page {safePage}/{pageCount}</span>
                  <div className="btn-group btn-group-sm">
                    <button className="btn btn-outline-secondary" disabled={safePage <= 1} onClick={() => setPage((value) => value - 1)}>Précédent</button>
                    <button className="btn btn-outline-secondary" disabled={safePage >= pageCount} onClick={() => setPage((value) => value + 1)}>Suivant</button>
                  </div>
                </div>
              )}
            </>
          )}

          {isOnline && pending > 0 && (
            <div className="offline-status__footer">
              <button
                type="button"
                className="btn btn-sm btn-primary w-100"
                onClick={() => syncOfflineQueue({ force: true })}
                disabled={syncing || preparing}
              >
                Synchroniser maintenant
              </button>
            </div>
          )}
          </aside>
        </>
      )}

      <button
        type="button"
        className={`btn btn-sm shadow offline-status__trigger ${statusButtonClass}`}
        onClick={() => setExpanded((value) => !value)}
        title="Gérer le mode hors ligne"
      >
        <FeatherIcon
          icon={!isOnline ? "wifi-off" : preparing || syncing ? "refresh-cw" : "database"}
          size="15"
          className="me-1"
        />
        {label}
      </button>
    </div>
  );
}

export default OfflineStatus;
